import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "@/contexts/AuthContext";
import { useParams } from "react-router-dom";

import { useFormHandler } from "@/core/hooks/useFormHandler";
import { PlayersService } from "@/core/http/services/PlayersService";
import { RankingService } from "@/core/http/services/RankingService";
import { MatchesService } from "@/core/http/services/MatchesService";
import { isCancelledMatch } from "@/core/utils/matches";

const normalizeMatch = (match) => ({
  id: match?.id,
  finished: match?.finished ?? false,
  status: match?.status ?? null,
  isCancelled: isCancelledMatch(match),
  teamA: (match?.teamA?.players || []).map((player) => ({
    name: player?.name || "Jogador",
    avatarUrl: player?.avatarUrl || null,
  })),
  teamB: (match?.teamB?.players || []).map((player) => ({
    name: player?.name || "Jogador",
    avatarUrl: player?.avatarUrl || null,
  })),
  result:
    match?.teamA?.score !== null &&
    match?.teamA?.score !== undefined &&
    match?.teamB?.score !== null &&
    match?.teamB?.score !== undefined
      ? {
          teamA: match.teamA.score,
          teamB: match.teamB.score,
        }
      : null,
});

const normalizeTopThree = (player) => ({
  id: player?.player?.id ?? player?.id,
  position: player?.position ?? null,
  name: player?.player?.name ?? player?.name ?? "Jogador",
  points: player?.points ?? 0,
  wins: player?.wins ?? 0,
  avatarUrl: player?.player?.avatarUrl ?? player?.avatarUrl ?? null,
});

const useHome = () => {
  const { handleChange, formData, setField, removeField } = useFormHandler();
  const { userLoggedData } = useContext(AuthContext);
  const playerId = userLoggedData?.playerId;
  const { clubId: clubIdFromUrl } = useParams();
  const clubId = clubIdFromUrl || userLoggedData?.selectedClubId || null;

  const [loading, setLoading] = useState({
    topThree: false,
    matches: false,
    savingResults: false,
  });
  const [error, setError] = useState({ type: null, message: null });

  const [dashboardRankings, setDashboardRankings] = useState([]);
  const [selectedRankingId, setSelectedRankingId] = useState(null);

  const [topThreeData, setTopThreeData] = useState([]);
  const [matchesData, setMatchesData] = useState([]);
  const [matches, setMatches] = useState([]);
  const [rankingId, setRankingId] = useState(null);
  const [roundEndsAt, setRoundEndsAt] = useState(null);
  const [isAvailable, setIsAvailable] = useState(null);
  const [isEditingResults, setIsEditingResults] = useState(false);
  const [resultDraft, setResultDraft] = useState({});

  const buildResultDraft = useCallback((nextMatches = []) => {
    const initialDraft = {};
    nextMatches.forEach((match) => {
      initialDraft[match.id] = {
        teamA: match.result?.teamA?.toString() || "",
        teamB: match.result?.teamB?.toString() || "",
      };
    });
    return initialDraft;
  }, []);

  const getDashboard = useCallback(async () => {
    if (!playerId || !clubId) {
      setDashboardRankings([]);
      setSelectedRankingId(null);
      setTopThreeData([]);
      setMatchesData([]);
      setRankingId(null);
      setRoundEndsAt(null);
      setIsAvailable(null);
      return;
    }

    setLoading((prev) => ({ ...prev, topThree: true, matches: true }));
    setError({ type: null, message: null });

    try {
      const { status, data, message } =
        await PlayersService.getDashboard(playerId, { clubId });

      if (data?.error || status !== 200) {
        setError({
          type: "dashboard",
          message: message || "Erro ao buscar os dados do dashboard.",
        });
        return;
      }

      const rankings = Array.isArray(data?.rankings) ? data.rankings : [];
      setDashboardRankings(rankings);

      if (!selectedRankingId && rankings.length > 0) {
        const firstId = rankings[0]?.ranking?.id ?? null;
        setSelectedRankingId(firstId);
      }
    } catch (err) {
      setError({
        type: "dashboard",
        message:
          err?.message || "Erro inesperado ao buscar os dados do dashboard",
      });
    } finally {
      setLoading((prev) => ({ ...prev, topThree: false, matches: false }));
    }
  }, [clubId, playerId, selectedRankingId]);

  const activeRanking = useMemo(() => {
    if (!dashboardRankings.length) return null;
    if (!selectedRankingId) return dashboardRankings[0] || null;
    return (
      dashboardRankings.find((item) => item?.ranking?.id === selectedRankingId) ||
      dashboardRankings[0] ||
      null
    );
  }, [dashboardRankings, selectedRankingId]);

  useEffect(() => {
    if (!dashboardRankings.length) {
      setTopThreeData([]);
      setMatchesData([]);
      setRankingId(null);
      setRoundEndsAt(null);
      setIsAvailable(null);
      return;
    }

    const nextRankingId = activeRanking?.ranking?.id ?? null;
    setRankingId(nextRankingId);
    setRoundEndsAt(activeRanking?.currentRound?.endsAt ?? null);
    setIsAvailable(
      activeRanking?.isAvailable !== undefined ? activeRanking.isAvailable : null,
    );
    setTopThreeData((activeRanking?.topThree || []).map(normalizeTopThree));
    setMatchesData(
      (activeRanking?.currentRound?.matches || []).map(normalizeMatch),
    );
  }, [activeRanking, dashboardRankings.length]);

  const initialize = useCallback(async () => {
    await Promise.all([getDashboard()]);
  }, [getDashboard]);

  useEffect(() => {
    const nextMatches = matchesData || [];
    setMatches(nextMatches);
    setResultDraft(buildResultDraft(nextMatches));
    setIsEditingResults(false);
  }, [buildResultDraft, matchesData]);

  const validMatches = useMemo(
    () => matches.filter((match) => !isCancelledMatch(match)),
    [matches],
  );
  const isWithoutSeason =
    !activeRanking ||
    (activeRanking?.currentRound === null &&
      Array.isArray(activeRanking?.topThree) &&
      activeRanking.topThree.length === 0);
  const emptySeasonMessage = isWithoutSeason
    ? "Voce ainda não esta inscrito em nenhum ranking. Aguarde a abertura da competição ou fale com a administração."
    : null;
  const hasSubmittedResults =
    validMatches.length > 0 && validMatches.every((match) => Boolean(match.result));
  const canSubmitAllResults =
    validMatches.length > 0 &&
    validMatches.every((match) => {
      const draft = resultDraft[match.id];
      return Boolean(draft?.teamA?.trim() && draft?.teamB?.trim());
    });
  const hasEditableMatches = validMatches.length > 0;

  const handleEditResults = useCallback(() => {
    if (!hasEditableMatches) return;
    if (hasSubmittedResults) return;
    setIsEditingResults(true);
  }, [hasEditableMatches, hasSubmittedResults]);

  const handleDraftChange = useCallback(
    (matchId, teamField) => (event) => {
      const match = matches.find((item) => item.id === matchId);
      if (isCancelledMatch(match)) return;
      const value = event.target.value;
      if (value !== "" && !/^\d+$/.test(value)) return;

      setResultDraft((prev) => ({
        ...prev,
        [matchId]: {
          ...prev[matchId],
          [teamField]: value,
        },
      }));
    },
    [matches],
  );

  const setAvailability = useCallback(
    async (nextAvailable) => {
      if (!clubId || !rankingId) return false;
      const { status } = await RankingService.updateMyAvailability({
        clubId,
        rankingId,
        isAvailable: nextAvailable,
      });
      const success = status === 200 || status === 204;
      if (success) setIsAvailable(nextAvailable);
      return success;
    },
    [clubId, rankingId],
  );

  const handleSaveResults = useCallback(async () => {
    if (!canSubmitAllResults) return;

    setLoading((prev) => ({ ...prev, savingResults: true }));
    setError({ type: null, message: null });

    try {
      const results = await Promise.all(
        validMatches.map((match) => {
          const draft = resultDraft[match.id];
          return MatchesService.updateResult(match.id, {
            team1Score: Number(draft.teamA),
            team2Score: Number(draft.teamB),
          });
        }),
      );

      const anyFailed = results.some(
        (r) => r.data?.error || (r.status !== 200 && r.status !== 204),
      );
      if (anyFailed) {
        setError({
          type: "saveResults",
          message: "Erro ao salvar um ou mais resultados. Tente novamente.",
        });
        return;
      }

      const updatedMatches = matches.map((match) => {
        if (isCancelledMatch(match)) return match;
        const draft = resultDraft[match.id];
        return {
          ...match,
          result: {
            teamA: Number(draft.teamA),
            teamB: Number(draft.teamB),
          },
        };
      });

      setMatches(updatedMatches);
      setIsEditingResults(false);
    } catch (err) {
      setError({
        type: "saveResults",
        message: err?.message || "Erro inesperado ao salvar os resultados.",
      });
    } finally {
      setLoading((prev) => ({ ...prev, savingResults: false }));
    }
  }, [canSubmitAllResults, matches, resultDraft, validMatches]);

  return {
    selectedRankingId,
    setSelectedRankingId,
    rankings: (dashboardRankings || []).map((item) => ({
      id: item?.ranking?.id ?? null,
      title: item?.ranking?.year
        ? `${item?.ranking?.name ?? "Ranking"} • ${item.ranking.year}`
        : item?.ranking?.name ?? "Ranking",
    })).filter((r) => Boolean(r.id)),

    topThreeData,
    matchesData,
    matches,
    isWithoutSeason,
    emptySeasonMessage,
    resultDraft,
    isEditingResults,
    hasSubmittedResults,
    canSubmitAllResults,
    hasEditableMatches,
    rankingId,
    roundEndsAt,
    isAvailable,
    setAvailability,
    error,
    loading,
    initialize,
    handleEditResults,
    handleDraftChange,
    handleSaveResults,
    setField,
    removeField,
    handleChange,
    formData,
  };
};

export default useHome;
